import { ref, watch, getCurrentInstance } from 'vue';

import DashboardService from '@/services/DashboardService';

import { ITEM_STATUS } from '@/components/Dashboard/common/WidgetSettingPanel/options/styles';
import { sortLongString } from '@/utils/dashboard';

const MIN_DELAY = 700;

const isCustomMetricKey = (key) => /^custom-metric:\d+/.test(key);
const isCustomBreakdownKey = (key) => !isNaN(key);

const getAssetId = (asset) => {
    return asset.assetId;
};

const getFieldKey = (field) => {
    return field.key;
};
const getName = (field) => {
    return field.locale;
};
const getAlias = (field) => {
    return field.alias;
};

const getDisplayName = (field) => {
    return getAlias(field) || getName(field) || null;
};

/**
 * Fetches field labels from BE for the current list keys.
 *
 * Falls back to FE locale (getMetricName / getBreakdownName) when the BE
 * returns nothing for a given key.
 *
 * NOTE: setup() runs inside Vue 2's data() initializer, before computed
 * properties are available. Props are initialized beforehand, so we read
 * selectedSource (prop) directly instead of assetIds (computed).
 *
 * @param {'metric'|'breakdown'} fieldType
 */
export function useFieldLabelsV3(fieldType) {
    const instance = getCurrentInstance();
    const fieldMap = ref({});
    const fieldLabelsLoading = ref(false);

    const isCustomKey =
        fieldType === 'metric' ? isCustomMetricKey : isCustomBreakdownKey;

    watch(
        () => {
            const vm = instance.proxy;
            // Serialize to a stable string so Vue compares with ===,
            // preventing spurious refetches when only array references change (e.g. sort order).
            return JSON.stringify({
                assetIds: sortLongString(
                    vm.selectedSource?.map(getAssetId) ?? []
                ),
                breakdowns: sortLongString(vm.currentBreakdownIds ?? []),
                keys: sortLongString((vm.currentList ?? []).map(getFieldKey)),
                widgetType: vm.isAnalytic ? 'normal' : 'marketing',
                language: vm.$i18n?.locale ?? 'en',
                isForDatastream: vm.isForDatastream ?? false,
                teamspaceId: vm.teamspaceId,
                viewId: vm.currentViewId,
            });
        },
        async (paramsKey) => {
            const {
                assetIds,
                breakdowns,
                keys,
                widgetType,
                language,
                isForDatastream,
                teamspaceId,
                viewId,
            } = JSON.parse(paramsKey);
            if (!assetIds.length || !keys.length) return;

            fieldLabelsLoading.value = true;
            try {
                const [result] = await Promise.all([
                    DashboardService.searchAvailableFieldsV3({
                        assetIds,
                        breakdowns,
                        fieldType,
                        widgetType,
                        language,
                        fieldKeysSearch: keys,
                        isForDatastream,
                        teamspaceId,
                        viewId,
                    }),
                    new Promise((resolve) => setTimeout(resolve, MIN_DELAY)),
                ]);

                const fields = result?.list ?? [];
                const fieldsByKey = fields.reduce((acc, field) => {
                    const fieldKey = getFieldKey(field);

                    if (fieldKey) {
                        acc[fieldKey] = field;
                    }

                    return acc;
                }, {});

                // Record every requested key: fieldInfo if found, null if not (= invalid).
                // Keys absent from fieldMap entirely = never fetched = new field (skeleton).
                fieldMap.value = keys.reduce((acc, key) => {
                    acc[key] = fieldsByKey[key] ?? null;
                    return acc;
                }, {});
            } catch (_) {
                // silently fall back to FE locale
            } finally {
                fieldLabelsLoading.value = false;
            }
        },
        { immediate: true }
    );

    // true  → key has never been included in a completed fetch (new field)
    // false → key was fetched before (may be valid or invalid/null)
    function isNewField(item) {
        const fieldKey = getFieldKey(item);
        return !!fieldKey && !(fieldKey in fieldMap.value);
    }

    // Resolves custom key status directly from the Vuex store.
    // Called during component render → Vue's render-time reactivity tracks
    // customMetricsMap / dimensionById and re-renders on changes automatically.
    function getCustomKeyStatus(key) {
        const vm = instance.proxy;
        if (fieldType === 'metric') {
            return vm.$store.getters['newDashboard/customMetricsMap'][key]
                ? ITEM_STATUS.NORMAL
                : ITEM_STATUS.INVALID;
        }
        return vm.$store.getters['newDashboard/dimensionById'][key]
            ? ITEM_STATUS.NORMAL
            : ITEM_STATUS.INVALID;
    }

    // Resolves custom key display name directly from the Vuex store.
    // Same render-time reactivity applies.
    function getCustomKeyName(key) {
        const vm = instance.proxy;
        if (fieldType === 'metric') {
            return (
                vm.$store.getters['newDashboard/customMetricsMap'][key]?.name ??
                null
            );
        }
        return (
            vm.$store.getters['newDashboard/dimensionById'][key]?.name ?? null
        );
    }

    function getFieldStatus(item) {
        const fieldKey = getFieldKey(item);
        if (!fieldKey) return ITEM_STATUS.INVALID;

        if (isCustomKey(fieldKey)) return getCustomKeyStatus(fieldKey);

        if (fieldLabelsLoading.value && isNewField(item))
            return ITEM_STATUS.LOADING;

        return fieldMap.value[fieldKey]
            ? ITEM_STATUS.NORMAL
            : ITEM_STATUS.INVALID;
    }

    function getFieldName(item) {
        const fieldKey = getFieldKey(item);
        if (!fieldKey) return null;

        if (isCustomKey(fieldKey)) return getCustomKeyName(fieldKey);

        // New field mid-loading → skeleton (empty text)
        if (fieldLabelsLoading.value && isNewField(item)) return '';

        const field = fieldMap.value[fieldKey];
        if (!field) return null;

        if (fieldType === 'metric') {
            // Alias takes priority over BE locale.
            // aliasesByIndex is accessed during render → reactive automatically.
            const vm = instance.proxy;
            const alias =
                vm.$store.getters['newDashboard/aliasesByIndex'][fieldKey]
                    ?.displayFieldName;
            return alias || getDisplayName(field);
        }

        return getDisplayName(field);
    }

    return { getFieldStatus, getFieldName, fieldLabelsLoading };
}
